import React from "react";
import { Controller, useWatch, useFormState } from "react-hook-form";
import {
  Card,
  CardContent,
  TextField,
  MenuItem,
  Box,
  Button,
} from "@mui/material";

// Location dropdown options
export const LOCATION_OPTIONS = [
  "Living Room / Sala",
  "Bedroom 1",
  "Bedroom 2",
  "Bedroom 3",
  "Kitchen",
  "Dining Area",
  "Bathroom 1",
  "Bathroom 2",
  "Closet / Cabinets",
  "Hallways",
  "Staircase",
  "Ceiling",
  "Baseboard",
  "Floor",
  "Exterior Perimeter",
  "Garage",
  "Garden",
  "Storage Area",
  "Other",
];

// Findings options
export const TERMITE_FINDINGS = [
  "No termite activity observed",
  "Active termite infestation found",
  "Previous signs of termite damage",
  "Mud tubes observed",
  "Termite droppings/frass found",
  "Damaged wood or hollow sound on tap",
  "Presence of termite swarmers or wings",
  "Termite feeding on bait",
];

// Action Taken options
export const ACTION_TAKEN_OPTIONS = [
  { value: "soil",       label: "Soil Treatment" },
  { value: "baiting",    label: "Baiting System Installation/ AG Monitoring in" },
  { value: "spraying",   label: "Spraying" },
  { value: "monitoring", label: "Monitoring only" },
];

export default function TermiteLocationCard({ control, register, index, removeLocation }) {
  const locationValue = useWatch({
    control,
    name: `locations.${index}.location`,
  }) || "";

  const actionTakenValue = useWatch({
    control,
    name: `locations.${index}.action_taken`,
  }) || "";

  // surface errors for this row
  const { errors } = useFormState({ control });
  const rowErrors = errors?.locations?.[index] || {};

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent>
        {/* Location (dropdown) */}
        <Controller
          control={control}
          name={`locations.${index}.location`}
          rules={{ required: "Location is required" }}
          render={({ field }) => (
            <TextField
              {...field}
              select
              label="Location"
              fullWidth
              size="small"
              sx={{ mb: 2 }}
              error={!!rowErrors?.location}
              helperText={rowErrors?.location?.message || ""}
            >
              {LOCATION_OPTIONS.map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {opt}
                </MenuItem>
              ))}
            </TextField>
          )}
        />

        {/* Free text if “Other” */}
        {locationValue === "Other" && (
          <TextField
            label="Specify other location"
            {...register(`locations.${index}.location_other`, {
              validate: (v) =>
                locationValue !== "Other" || String(v || "").trim().length > 0 || "Please specify other location",
            })}
            fullWidth
            size="small"
            sx={{ mt: -1, mb: 2 }}
            error={!!rowErrors?.location_other}
            helperText={rowErrors?.location_other?.message || ""}
          />
        )}

        {/* Findings */}
        <Controller
          control={control}
          name={`locations.${index}.findings`}
          rules={{ required: "Findings is required" }}
          render={({ field }) => (
            <TextField
              {...field}
              select
              label="Findings"
              fullWidth
              size="small"
              sx={{ mt: 1 }}
              error={!!rowErrors?.findings}
              helperText={rowErrors?.findings?.message || ""}
            >
              {TERMITE_FINDINGS.map((label) => (
                <MenuItem key={label} value={label}>
                  {label}
                </MenuItem>
              ))}
            </TextField>
          )}
        />

        {/* Action Taken */}
        <Controller
          control={control}
          name={`locations.${index}.action_taken`}
          rules={{ required: "Action Taken is required" }}
          render={({ field }) => (
            <TextField
              {...field}
              select
              label="Action Taken"
              fullWidth
              size="small"
              sx={{ mt: 2 }}
              error={!!rowErrors?.action_taken}
              helperText={rowErrors?.action_taken?.message || ""}
            >
              {ACTION_TAKEN_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
          )}
        />

        {/* If baiting/AG is selected, ask for the exact place */}
        {actionTakenValue === "baiting" && (
          <TextField
            label="Bait/Monitor Location"
            {...register(`locations.${index}.bait_monitor_location`, {
              validate: (v) =>
                  actionTakenValue !== "baiting" || String(v || "").trim().length > 0 || "Please specify bait/monitor location",
            })}
            fullWidth
            size="small"
            sx={{ mt: 1 }}
            placeholder="Specify the location"
            error={!!rowErrors?.bait_monitor_location}
            helperText={rowErrors?.bait_monitor_location?.message || ""}
          />
        )}

        {/* Remarks */}
        <TextField
          label="Remarks"
          {...register(`locations.${index}.remarks`, {
            validate: (v) => (String(v || "").trim().length > 0) || "Remarks is required",
          })}
          fullWidth
          size="small"
          multiline
          minRows={2}
          sx={{ mt: 2 }}
          error={!!rowErrors?.remarks}
          helperText={rowErrors?.remarks?.message || ""}
        />

        {/* Remove this location */}
        <Box textAlign="right" sx={{ mt: 2 }}>
          <Button color="error" size="small" onClick={() => removeLocation(index)}>
            Remove Location
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}
